import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setSelectedWalk } from "./sparcWalkSlice";
import { AnyAction, Dispatch } from "redux";

interface ValidationErrors {
  employeeId?: boolean;
  behaviors?: boolean;
  questions?: boolean;
  expectedAnswers?: boolean;
  observations?: boolean;
  possibleSolutions?: boolean;
  signature?: boolean;
}

interface SparcImage {
  imageId: string;
  imageBase64: string | null;
  imagePreview: string | null;
  description: string;
  observationId: string;
}

export interface MiscObservationImage {
  imageUrl: string | null;
  fileName: string | null;
  description: string;
  index: number;
}

interface SparcWalkInspectionState {
  walkState: "new" | "in-progress" | "completed";
  selectedBehaviorIds: string[];
  selectedQuestionIds: string[];
  expectedAnswers: string;
  observations: string;
  possibleSolutions: string;
  selectedEmployeeId: string | null;
  managerSignatureFileName: string | null;
  managerSignatureImageUrl: string | null;
  isManagerSignatureUpdated: boolean;
  miscNotes: string;
  miscObservationImages: MiscObservationImage[];
  miscellaneousObservations: string;
  validationErrors: ValidationErrors;
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
  images: SparcImage[];
  completeWalkData: any; // Store complete API response data
}

const initialState: SparcWalkInspectionState = {
  walkState: "new",
  selectedBehaviorIds: [],
  selectedQuestionIds: [],
  expectedAnswers: "",
  observations: "",
  possibleSolutions: "",
  selectedEmployeeId: null,
  // Updated signature fields
  managerSignatureFileName: null,
  managerSignatureImageUrl: null,
  isManagerSignatureUpdated: false,
  miscNotes: "",
  miscObservationImages: [],
  miscellaneousObservations: "",
  validationErrors: {},
  isSubmitting: false,
  error: null,
  successMessage: null,
  images: [],
  completeWalkData: null, // Initialize as null
};

const sparcWalkInspectionSlice = createSlice({
  name: "sparcWalkInspection",
  initialState,
  reducers: {
    setWalkState(
      state,
      action: PayloadAction<"new" | "in-progress" | "completed">
    ) {
      state.walkState = action.payload;
    },
    setSelectedBehaviorIds(state, action: PayloadAction<string[]>) {
      state.selectedBehaviorIds = action.payload;
    },
    setSelectedQuestionIds(state, action: PayloadAction<string[]>) {
      state.selectedQuestionIds = action.payload;
    },
    setExpectedAnswers(state, action: PayloadAction<string>) {
      state.expectedAnswers = action.payload;
    },
    setObservations(state, action: PayloadAction<string>) {
      state.observations = action.payload;
    },
    setPossibleSolutions(state, action: PayloadAction<string>) {
      state.possibleSolutions = action.payload;
    },
    setSelectedEmployeeId(state, action: PayloadAction<string | null>) {
      state.selectedEmployeeId = action.payload;
    },
    setManagerSignatureFileName(state, action: PayloadAction<string | null>) {
      state.managerSignatureFileName = action.payload;
    },
    setManagerSignatureImageUrl(state, action: PayloadAction<string | null>) {
      state.managerSignatureImageUrl = action.payload;
    },
    setIsManagerSignatureUpdated(state, action: PayloadAction<boolean>) {
      state.isManagerSignatureUpdated = action.payload;
    },
    setMiscNotes(state, action: PayloadAction<string>) {
      state.miscNotes = action.payload;
    },
    setValidationErrors(state, action: PayloadAction<ValidationErrors>) {
      state.validationErrors = action.payload;
    },
    setIsSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setSuccessMessage(state, action: PayloadAction<string | null>) {
      state.successMessage = action.payload;
    },
    clearImages(state) {
      // Reset just the images array
      state.images = [];
    },
    resetInspection(state) {
      // Reset everything except walkState
      const currentWalkState = state.walkState;

      // Explicitly reset all fields to make sure nested objects are fully cleared
      state.selectedBehaviorIds = [];
      state.selectedQuestionIds = [];
      state.expectedAnswers = "";
      state.observations = "";
      state.possibleSolutions = "";
      state.selectedEmployeeId = null;

      // Explicitly reset signature related fields
      state.managerSignatureFileName = null;
      state.managerSignatureImageUrl = null;
      state.isManagerSignatureUpdated = false;

      // Explicitly reset miscellaneous fields
      state.miscNotes = "";
      state.miscObservationImages = []; // Reset the array completely
      state.miscellaneousObservations = "";

      // Reset validation errors and submission state
      state.validationErrors = {};
      state.isSubmitting = false;
      state.error = null;
      state.successMessage = null;

      // Reset images array
      state.images = [];

      // Reset complete walk data
      state.completeWalkData = null;

      // Preserve walk state
      state.walkState = currentWalkState;
    },
    updateSparcWalkImage(state, action: PayloadAction<SparcImage>) {
      const { imageId, observationId, imageBase64, imagePreview, description } =
        action.payload;

      // Log for debugging
      console.log(
        `Redux: Updating image for observationId=${observationId}, imageId=${imageId}`
      );

      // Find existing image
      const existingImageIndex = state.images.findIndex(
        (img) => img.observationId === observationId && img.imageId === imageId
      );

      if (existingImageIndex >= 0) {
        // Log for debugging
        console.log(
          `Redux: Found existing image at index ${existingImageIndex}, updating it`,
          {
            oldImage: state.images[existingImageIndex],
            newData: { imageBase64, imagePreview, description },
          }
        );

        // Update existing image - use the new values, not conditionals
        state.images[existingImageIndex] = {
          ...state.images[existingImageIndex],
          imageBase64: imageBase64,
          imagePreview: imagePreview,
          description:
            description || state.images[existingImageIndex].description,
        };
      } else {
        // Log for debugging
        console.log("Redux: No existing image found, adding new image", {
          newImage: {
            imageId,
            observationId,
            imageBase64,
            imagePreview,
            description,
          },
        });

        // Add new image
        state.images.push({
          imageId,
          observationId,
          imageBase64,
          imagePreview,
          description,
        });
      }
    },
    updateSparcWalkImageDescription(
      state,
      action: PayloadAction<{
        observationId: string;
        imageId: string;
        description: string;
      }>
    ) {
      const { observationId, imageId, description } = action.payload;
      const image = state.images.find(
        (img) => img.observationId === observationId && img.imageId === imageId
      );
      if (image) {
        image.description = description;
      }
    },
    clearSparcWalkImages(state) {
      state.images = [];
    },
    setManagerSignature(
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) {
      state.managerSignatureFileName = action.payload.fileName;
      state.managerSignatureImageUrl = action.payload.imageUrl || null;
      if (action.payload.isNewUpload) {
        state.isManagerSignatureUpdated = true;
      } else if (action.payload.isNewUpload === false) {
        state.isManagerSignatureUpdated = false;
      }
    },
    resetManagerSignatureFlag(state) {
      state.isManagerSignatureUpdated = false;
    },
    setMiscObservationImage(
      state,
      action: PayloadAction<{ index: number; image: MiscObservationImage }>
    ) {
      if (!state.miscObservationImages) {
        state.miscObservationImages = [];
      }
      state.miscObservationImages[action.payload.index] = action.payload.image;
    },
    setMiscellaneousObservations(state, action: PayloadAction<string>) {
      state.miscellaneousObservations = action.payload;
    },
    setCompleteWalkData(state, action: PayloadAction<any>) {
      state.completeWalkData = action.payload;
    },
  },
});

// Selectors for easy access to walk data from any component
export const selectCompleteWalkData = (state: any) =>
  state.sparcWalkInspection.completeWalkData;

export const {
  setWalkState,
  setSelectedBehaviorIds,
  setSelectedQuestionIds,
  setExpectedAnswers,
  setObservations,
  setPossibleSolutions,
  setSelectedEmployeeId,
  setManagerSignatureFileName,
  setManagerSignatureImageUrl,
  setIsManagerSignatureUpdated,
  setMiscNotes,
  setMiscellaneousObservations,
  setValidationErrors,
  setIsSubmitting,
  setError,
  setSuccessMessage,
  clearImages,
  resetInspection,
  updateSparcWalkImage,
  updateSparcWalkImageDescription,
  clearSparcWalkImages,
  setManagerSignature,
  resetManagerSignatureFlag,
  setMiscObservationImage,
  setCompleteWalkData,
} = sparcWalkInspectionSlice.actions;

// Export resetBothInspectionAndSelectedWalk thunk

export default sparcWalkInspectionSlice.reducer;

// Create a thunk that will reset both the inspection state and the selectedWalk state
export const resetBothInspectionAndSelectedWalk = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    // Reset the inspection state
    dispatch(resetInspection());

    // Reset the selectedWalk in sparcWalkSlice
    dispatch(setSelectedWalk(null));

    console.log(
      "Both sparcWalkInspection state and selectedWalk have been reset"
    );
  };
};
